import axios, { ResponseType } from 'axios';
import moment from 'moment';

import { AuthenticationError, AuthorizationError, LargePayloadError, ServerError } from '@/libs/error';
import { AdditionalInfo, AdditionalInfoExpansion } from '@/libs/llc/database/additional-info';
import { Campaign } from '@/libs/llc/database/campaign';
import { authenticate } from '@/libs/store';
import { buildQueryParameterWithObject } from '@/libs/utility/net/url';
import Store from '@/store';
import type { ReqSpecialDayRemark } from '@shared/types';


// これは不要になったので削除する
export async function siteverifyRecaptcha(response: string) {
  return;
  try {
    const path = `/v1/api/recaptcha`;
    await request(path, 'post', { response });
    return;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError();
  }
}

export async function getAuth() {
  try {
    const path = '/v1/api/auth';
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError();
  }
}

export interface AuthManagement {
  cognitoUserName: string;
  email: string;
  userName: string;
  tel: string;
  targetName: string[];
  authority: string[];
}

export async function getAuthManagement(params: { targetName: string }): Promise<AuthManagement[]> {
  const { targetName } = params;
  try {
    const path = '/v1/api/management/auth';
    const query = `?targetName=${targetName}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function createAuthManagement(body: {
  email: string;
  userName: string;
  tel: string;
  targetName: string[];
  authority: string[];
}) {
  try {
    const path = '/v1/api/management/auth';
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function updateAuthManagement(
  params: { cognitoUserName: string },
  body: { userName: string; tel: string; targetName: string[]; authority: string[] },
) {
  const { cognitoUserName } = params;
  try {
    const path = `/v1/api/management/auth/${cognitoUserName}`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function deleteAuthManagement(params: { cognitoUserName: string }) {
  const { cognitoUserName } = params;
  try {
    const path = `/v1/api/management/auth/${cognitoUserName}`;
    const response = await request(path, 'delete');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface Account {
  targetName: string;
  encodedTargetName: string;
  cpAccountId: string;
  cpPassword: string;
  lineAccessToken: string;
  lineChannelSecret: string;
  miniAppLiffId: string;
  miniAppChannelId: string;
  miniAppChannelSecret: string;
  linePayChannelId: string;
  linePayChannelSecretId: string;
  stripePublicKey: string;
  stripeSecretToken: string;
  paypayClientId: string;
  paypayClientSecret: string;
  paypayMerchantId: string;
}

export async function getAccount(param: { targetName: string }): Promise<Account> {
  const { targetName } = param;
  try {
    const path = `/v1/api/account/${targetName}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError();
  }
}

export async function updateAccount(param: { targetName: string }, body: Account) {
  const { targetName } = param;
  try {
    const path = `/v1/api/account/${targetName}`;
    await request(path, 'put', body);
  } catch (e) {
    throw new ServerError();
  }
}

export interface MessageCount {
  targetName: string;
  timestamp: number; // unix timestamp
  consumed: number;
  quota: number;
}

export async function getLatestMessageCount(param: { targetName: string }): Promise<MessageCount | null> {
  const { targetName } = param;
  try {
    const path = `/v1/api/message-count/${targetName}/latest`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError();
  }
}

export type Property = any;

export async function getProperties(): Promise<Property[]> {
  try {
    const path = `/v1/api/property`;
    const response = await request(path, 'get', {}, { getIp: true });
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        throw new Error(e.response.data.msg);
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function getProperty(param: { targetName: string }): Promise<Property> {
  const { targetName } = param;
  try {
    const path = `/v1/api/property/${targetName}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export async function updateProperty(param: { targetName: string }, body: Property) {
  const { targetName } = param;
  try {
    const path = `/v1/api/property/${targetName}`;
    await request(path, 'put', body);
  } catch (e) {
    throw new ServerError();
  }
}

export interface PropertyTerms {
  sharedUrl: string;
}

export async function uploadPropertyTerms(param: { targetName: string, fileType: string }, file: File): Promise<PropertyTerms> {
  try {
    const { targetName , fileType = '1'} = param;
    const path = `/v1/api/property/${targetName}/upload?fileType=${fileType}`;
    const body = new FormData();
    body.append('file', file);
    const response = await request(path, 'put', body, { upload: true });
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 413) {
        throw new LargePayloadError();
      }
    }
    throw new ServerError(e);
  }
}

export interface PropertyManagement {
  targetName: string;
  enabled: boolean;
  propertyName: string;
}

export async function getAllProperties(): Promise<PropertyManagement[]> {
  try {
    const path = `/v1/api/management/property`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function createProperty(body: any) {
  try {
    const path = `/v1/api/management/property`;
    await request(path, 'post', body);
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function archiveProperty(param: { targetName: string }) {
  const { targetName } = param;
  try {
    const path = `/v1/api/management/property/${targetName}/archive`;
    await request(path, 'put');
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function restoreProperty(param: { targetName: string }) {
  const { targetName } = param;
  try {
    const path = `/v1/api/management/property/${targetName}/restore`;
    await request(path, 'put');
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface Device {
  deviceId: number;
  deviceName: string;
  enablePin: boolean;
  dummyLock: boolean;
  text: string;
}

export async function getDevices(param: { targetName: string }): Promise<Device[]> {
  const { targetName } = param;
  try {
    const path = `/v1/api/devices/${targetName}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export async function getDevice(param: { targetName: string; deviceId: string }): Promise<Device | null> {
  const { targetName, deviceId } = param;
  try {
    const path = `/v1/api/devices/${targetName}/${deviceId}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export async function getRooms(param: { targetName: string }): Promise<any[]> {
  const { targetName } = param;
  try {
    const path = `/v1/api/rooms`;
    const query = `?targetName=${targetName}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export interface RoomImage {
  sharedUrl: string;
}

export async function uploadRoomImage(param: { roomId: string }, file: File): Promise<RoomImage> {
  const { roomId } = param;
  try {
    const path = `/v1/api/rooms/${roomId}/upload`;
    const body = new FormData();
    body.append('file', file);
    const response = await request(path, 'put', body, { upload: true });
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 413) {
        throw new LargePayloadError();
      }
    }
    throw new ServerError();
  }
}

export async function createRoom(body: any) {
  try {
    const path = `/v1/api/rooms`;
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function updateRoom(param: { roomId: string }, body: any) {
  const { roomId } = param;
  try {
    const path = `/v1/api/rooms/${roomId}`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
    }
    throw new ServerError();
  }
}

export async function deleteRoom(param: { roomId: string }) {
  const { roomId } = param;
  try {
    const path = `/v1/api/rooms/${roomId}`;
    const response = await request(path, 'delete');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function getSpecialDays(targetName: string) {
  try {
    const path = `/v1/api/rooms/special_day/${targetName}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError();
  }
}
export async function updateSpecialDay(targetName: string, body: any) {
  try {
    const path = `/v1/api/rooms/special_day/${targetName}`;
    const response = await request(path, 'post', body);
    return true;
  } catch (e) {
    if (e.response) {
      if (e.response.status !== 200) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
    }
    throw new ServerError();
  }
}

export async function deleteSpecialDay(targetName: string, body: any) {
  try {
    const path = `/v1/api/rooms/special_day/${targetName}`;
    await request(path, 'delete', body);
    return true;
  } catch (e) {
    if (e.response) {
      if (e.response.status !== 200) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
    }
    throw new ServerError();
  }
}

export async function uploadCsvApi(targetName, csv): Promise<any> {
  try {
    const path = `/v1/api/rooms/special_day/${targetName}/upload-csv`;
    const body = new FormData();
    body.append('file', csv);

    const response =  await request(path, 'post', body, { upload: true });
    return response.data;
  } catch (error) {
    console.error('CSVアップロード中にエラーが発生しました:', error);
    throw error;
  }
}


export interface BusinessSchedule {
  roomId: string;
  productName: string;
  closed: boolean;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  changed: boolean;
}

export async function getBusinessDay(param: {
  roomId: string;
  startDate: string;
  endDate: string;
}): Promise<BusinessSchedule[]> {
  const { roomId, startDate, endDate } = param;
  try {
    const path = `/v1/api/rooms/${roomId}/business-day`;
    const query = `?startDate=${startDate}&endDate=${endDate}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export interface UpdateBusinessDayRequest {
  roomId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  startTime?: string; // HH:mm
  endTime?: string; // HH:mm
  holiday: boolean;
}

export async function updateBusinessDay(param: UpdateBusinessDayRequest) {
  const { roomId, ...body } = param;
  try {
    const path = `/v1/api/rooms/${roomId}/business-day`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export async function getResources(param: { targetName: string }): Promise<any[]> {
  const { targetName } = param;
  try {
    const path = `/v1/api/resources/${targetName}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function createResource(param: { targetName: string }, body: any) {
  const { targetName } = param;
  try {
    const path = `/v1/api/resources/${targetName}`;
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface ResourceImage {
  sharedUrl: string;
}

export async function uploadResourceImage(
  param: { targetName: string; resourceId: string },
  file: File,
): Promise<ResourceImage> {
  const { targetName, resourceId } = param;
  try {
    const path = `/v1/api/resources/${targetName}/${resourceId}/upload`;
    const body = new FormData();
    body.append('file', file);
    const response = await request(path, 'put', body, { upload: true });
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
      if (e.response.status === 413) {
        throw new LargePayloadError();
      }
    }
    throw new ServerError();
  }
}

export async function updateResource(param: { targetName: string; resourceId: string }, body: any) {
  const { targetName, resourceId } = param;
  try {
    const path = `/v1/api/resources/${targetName}/${resourceId}`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function deleteResource(param: { targetName: string; resourceId: string }, body: any) {
  const { targetName, resourceId } = param;
  try {
    const path = `/v1/api/resources/${targetName}/${resourceId}`;
    const response = await request(path, 'delete', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface ResourceUsageSchedule {
  resourceId: string;
  resourceName: string;
  assignmentName: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
}

export async function getResourceUsages(param: {
  targetName: string;
  resourceId: string;
  startDate: string;
  endDate: string;
}): Promise<ResourceUsageSchedule[]> {
  const { targetName, resourceId, startDate, endDate } = param;
  try {
    const path = `/v1/api/resources/${targetName}/${resourceId}/usages`;
    const query = `?startDate=${startDate}&endDate=${endDate}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export interface ResourceBusinessSchedule {
  resourceId: string;
  resourceName: string;
  holiday: boolean;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  changed: boolean;
}

export async function getResourceBusinessDay(param: {
  targetName: string;
  resourceId: string;
  startDate: string;
  endDate: string;
}): Promise<ResourceBusinessSchedule[]> {
  const { targetName, resourceId, startDate, endDate } = param;
  try {
    const path = `/v1/api/resources/${targetName}/${resourceId}/business-day`;
    const query = `?startDate=${startDate}&endDate=${endDate}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export interface UpdateResourceBusinessDayRequest {
  targetName: string;
  resourceId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  startTime?: string; // HH:mm
  endTime?: string; // HH:mm
  holiday: boolean;
}

export async function updateResourceBusinessDay(param: UpdateResourceBusinessDayRequest) {
  const { targetName, resourceId, ...body } = param;
  try {
    const path = `/v1/api/resources/${targetName}/${resourceId}/business-day`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export interface GetResourceOnSiteResponse {
  id: string;
  targetName: string;
  name: string;
  imageName?: string;
  imageUrl?: string;
  price?: number;
  membershipPrice?: number;
  qrCodeImageUrl: string;
}

export async function getResourcesOnSite(param: { targetName: string }): Promise<GetResourceOnSiteResponse[]> {
  const { targetName } = param;
  try {
    const path = `/v1/api/resources-on-site`;
    const query = `?targetName=${targetName}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export interface CreateResourceOnSiteRequest {
  targetName: string;
  name: string;
  imageId?: string;
  imageName?: string;
  imageUrl?: string;
  price: number;
  membershipPrice: number;
  consumptionTaxRate: number;
}

export async function createResourceOnSite(param: CreateResourceOnSiteRequest) {
  const { ...body } = param;
  try {
    const path = `/v1/api/resources-on-site`;
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface UpdateResourceOnSiteRequest {
  id: string;
  targetName: string;
  name: string;
  imageId?: string;
  imageName?: string;
  imageUrl?: string;
  price: number;
  membershipPrice: number;
  consumptionTaxRate: number;
}

export async function updateResourceOnSite(param: UpdateResourceOnSiteRequest) {
  const { id, ...body } = param;
  try {
    const path = `/v1/api/resources-on-site/${id}`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface DeleteResourceOnSiteRequest {
  id: string;
}

export async function deleteResourceOnSite(param: DeleteResourceOnSiteRequest) {
  const { id } = param;
  try {
    const path = `/v1/api/resources-on-site/${id}`;
    const response = await request(path, 'delete');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

interface UploadResouceOnSiteImageResponse {
  id: string;
  name: string;
  url: string;
}

export async function uploadResourceOnSiteImage(file: File): Promise<UploadResouceOnSiteImageResponse> {
  try {
    const path = `/v1/api/resources-on-site/images`;
    const body = new FormData();
    body.append('file', file);
    const response = await request(path, 'post', body, { upload: true });
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
      if (e.response.status === 413) {
        throw new LargePayloadError();
      }
    }
    throw new ServerError();
  }
}

export interface GetShoppingHistoryResponse {
  id: string;
  timestamp: number; // unix timestamp
  targetName: string;
  userId: string;
  membershipAttributes: { [key: string]: string };
  receiptUrl: string;
  items: {
    id: string;
    name: string;
    price: number;
    count: number;
  }[];
  amount: number;
  paytype: 'linepay' | 'stripe';
  transactionId: string;
}

export async function getShoppingHistory(param: {
  targetName: string;
  date: string;
}): Promise<GetShoppingHistoryResponse[]> {
  const { targetName, date } = param;
  try {
    const path = `/v1/api/shopping/history`;
    const query = `?targetName=${targetName}&date=${date}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function getResourceReservations(param: { targetName: string }): Promise<any[]> {
  const { targetName } = param;
  try {
    const path = `/v1/api/resource/reservations/${targetName}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function createResourceReservation(param: { targetName: string; resourceId: string }, body: any) {
  const { targetName, resourceId } = param;
  try {
    const path = `/v1/api/resource/reservations/${targetName}/${resourceId}`;
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function updateResourceReservation(param: { targetName: string; resourceId: string }, body: any) {
  const { targetName, resourceId } = param;
  try {
    const path = `/v1/api/resource/reservations/${targetName}/${resourceId}`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function deleteResourceReservation(param: { targetName: string; resourceId: string }, body: any) {
  const { targetName, resourceId } = param;
  try {
    const path = `/v1/api/resource/reservations/${targetName}/${resourceId}`;
    const response = await request(path, 'delete', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function getRecentReservations(param: { targetName: string; roomIds: string[] }): Promise<any[]> {
  const { targetName, roomIds } = param;
  try {
    const path = '/v1/api/reservations/recent';
    const query = `?targetName=${targetName}&roomId=${roomIds.join(',')}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export async function getTemporaryReservationsHistory(targetName: string): Promise<any[]> {
    try {
        const path = '/v1/api/reservations/temporary/history';
        const query = `?targetName=${targetName}`;
        const response = await request(path + query, 'get');
        return response.data;
    } catch (e) {
        throw new ServerError(e);
    }
}

export async function getSpecialDayConflictDates(param: { targetName: string; days: string[]; specialDayId: number; isAll?: boolean; }): Promise<any[]> {
  const { targetName, days, specialDayId, isAll } = param;
  const body = { days, specialDayId, isAll };
  try {
    const path = `/v1/api/rooms/special_day/${targetName}/check_date`;
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export interface CalendarReservation {
  reservation: any;
  events: {
    start: string; // YYYY-MM-DDTHH:mm
    end: string; // YYYY-MM-DDTHH:mm
  }[];
}

export async function getReservations(param: {
  targetName: string;
  roomIds: string[];
  date: string;
  status: string
}): Promise<CalendarReservation[]> {
  const { targetName, roomIds, date, status } = param;
  try {
    const path = '/v1/api/reservations';
    const query = `?targetName=${targetName}&roomId=${roomIds.join(',')}&date=${date}&status=${status}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function createAdminReservation(body: any) {
  try {
    const path = '/v1/api/reservations/admin';
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function updateAdminReservation(body: any) {
  try {
    const path = '/v1/api/reservations/admin';
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function deleteAdminReservation(body: any) {
  try {
    const path = '/v1/api/reservations/admin';
    const response = await request(path, 'delete', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface ReservationFrame {
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  empty: boolean;
  warning: boolean;
}

export async function getUserReservationFrames(param: {
  targetName: string;
  roomId: string;
  date: string;
}): Promise<ReservationFrame[]> {
  const { targetName, roomId, date } = param;
  try {
    const path = `/v1/api/reservations/user/frames`;
    const query = `?targetName=${targetName}&roomId=${roomId}&date=${date}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export interface ReservationFrameParsed {
  start: Date;
  end: Date;
  empty: boolean;
  warning: boolean;
}

export function parseReservationFrame(range: ReservationFrame): ReservationFrameParsed {
  const { date, startTime, endTime, empty, warning } = range;
  const start = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
  const end = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm').toDate();
  return { start, end, empty, warning };
}

export async function createUserReservation(body: any) {
  try {
    const path = '/v1/api/reservations/user';
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function updateUserReservation(body: any) {
  try {
    const path = '/v1/api/reservations/user';
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.msg) {
          throw new Error(e.response.data.msg);
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function deleteUserReservation(body: any) {
  try {
    const path = '/v1/api/reservations/user';
    const response = await request(path, 'delete', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface CsvUserReservation {
  assignmentId: number;
  propertyName: string;
  productName: string;
  assignmentName: string;
  start: string; // YYYY-MM-DDTHH:mm
  end: string; // YYYY-MM-DDTHH:mm
  checkInDateTime?: string;
  userId: string;
  email: string;
  amount: number;
  refundAmount: number;
  campaignCode: string;
  status: 'reserved' | 'canceled';
  cancelType: 'user' | 'auto' | 'admin';
  membershipAttributes: { [key: string]: string };
  additionalInfo?: AdditionalInfo;
  additionalInfoExpansion?: AdditionalInfoExpansion;
}

export async function getCsvUserReservations(param: {
  targetName: string;
  roomIds: string[];
  date: string;
  status: string;
}): Promise<CsvUserReservation[]> {
  const { targetName, roomIds, date, status } = param;
  try {
    const path = '/v1/api/reservations/user/csv';
    const query = `?targetName=${targetName}&roomId=${roomIds.join(',')}&date=${date}&status=${status}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError(e);
  }
}

export interface GetUserReservationReceiptResponse {
  receiptUrl: string;
}

export async function getUserReservationReceipt(param: {
  assignmentId: number;
}): Promise<GetUserReservationReceiptResponse> {
  const { assignmentId } = param;
  try {
    const path = `/v1/api/reservations/user/receipts/${assignmentId}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 404) {
        throw new Error('Requested receipt is not found.');
      }
    }
    throw new ServerError(e);
  }
}

export interface GetUserReservationReceiptsResponse {
  downloadUrl: string;
}

export async function getUserReservationReceipts(param: {
  targetName: string;
  roomIds: string[];
  date: string;
}): Promise<GetUserReservationReceiptsResponse> {
  const { targetName, roomIds, date } = param;
  try {
    const path = '/v1/api/reservations/user/receipts';
    const query = `?targetName=${targetName}&roomId=${roomIds.join(',')}&date=${date}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    throw new ServerError(e);
  }
}

export interface User {
  targetName: string;
  userId: string;
  latestDateTime: string;
  productName: string;
  reservationCount: number;
  cancelCount: number;
  membership: boolean;
  membershipAttributes: { [key: string]: string };
  customerId: string;
  additionalInfo?: {
    numOfPeople: string;
    name: string;
    nameRuby: string;
    telephone: string;
    email: string;
    additionalEmail: string;
    gender: string;
    birthday: string;
    address: string;
    consultationMemo: string;
  };
  additionalInfoExpansion?: {
    type: string;
    values: string[];
  };
  isBlock: boolean;
  isWebUser: boolean;
}

export async function getUsers(param: { targetName: string }): Promise<User[] | null> {
  const { targetName } = param;
  const path = `/v1/api/users/${targetName}`;
  const response = await request(path, 'get');
  return response.data;
}

export interface UpdateUserRequest {
  targetName: string;
  userId: string;
  membershipAttributes: { [key: string]: string };
}

export async function updateUser(param: UpdateUserRequest) {
  const { targetName, userId, membershipAttributes } = param;
  try {
    const path = `/v1/api/users/${targetName}/${userId}`;
    const body = { membershipAttributes };
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError();
  }
}

export interface UpdateUsersIsBlockRequest {
  targetName: string;
  userId: string;
  isBlock: boolean;
}

export async function updateUsersIsBlock(param: UpdateUsersIsBlockRequest) {
  const { targetName, userId, isBlock } = param;
  try {
    const path = `/v1/api/users/${targetName}/${userId}/block`;
    const body = { isBlock };
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError();
  }
}

export interface UpdateMembershipRequest {
  targetName: string;
  userId: string;
  membership: boolean;
}

export async function updateMembership(param: UpdateMembershipRequest) {
  const { ...body } = param;
  try {
    const path = '/v1/api/membership';
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface GetMembershipAuthResponse {
  id: string;
  timestamp: number; // unix timestamp
  targetName: string;
  email: string;
  attributes: { [key: string]: string };
  code: string;
  status: 'issued' | 'canceled' | 'authenticated' | 'expired';
}

export async function getMembershipAuthentications(param: {
  targetName: string;
}): Promise<GetMembershipAuthResponse[]> {
  const { targetName } = param;
  try {
    const path = '/v1/api/membership/auth';
    const query = `?targetName=${targetName}`;
    const response = await request(path + query, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export interface CreateMembershipAuthRequest {
  targetName: string;
  email: string;
  attributes: { [key: string]: string };
}

export async function createMembershipAuthentication(param: CreateMembershipAuthRequest) {
  const { ...body } = param;
  try {
    const path = '/v1/api/membership/auth';
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface UpdateMembershipAuthRequest {
  id: string;
  attributes: { [key: string]: string };
}

export async function updateMembershipAuthentication(param: UpdateMembershipAuthRequest) {
  const { id, attributes } = param;
  try {
    const path = `/v1/api/membership/auth/${id}`;
    const body = { attributes };
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.code === 'invalid_status') {
          throw new Error('Membership authentication code status is invalid.');
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export interface CancelMembershipAuthRequest {
  id: string;
}

export async function cancelMembershipAuthentication(param: CancelMembershipAuthRequest) {
  const { id } = param;
  try {
    const path = `/v1/api/membership/auth/${id}/cancel`;
    const response = await request(path, 'post');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data && e.response.data.code === 'invalid_status') {
          throw new Error('Membership authentication code status is invalid.');
        }
      }
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function getAggregate(param: { targetName: string; roomIds: string[] }) {
  const { targetName, roomIds } = param;
  const roomId = roomIds.join(',');
  const path = '/v1/api/aggregate';
  const query = `?targetName=${targetName}&roomId=${roomId}`;
  const response = await request(path + query, 'get');
  return response.data;
}

export async function getLockControl(param: { targetName: string; deviceId: string }) {
  const { targetName, deviceId } = param;
  const path = `/v1/api/lock/control/${targetName}/${deviceId}`;
  const response = await request(path, 'get');
  return response.data;
}

export async function updateLockControl(param: { targetName: string; deviceId: string }, body: { on: string }) {
  const { targetName, deviceId } = param;
  const path = `/v1/api/lock/control/${targetName}/${deviceId}`;
  const response = await request(path, 'put', body);
  return response.data;
}

export async function getNotification() {
  try {
    const path = '/v1/api/notification';
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function getIsReservationExtend(targetName, assignmentId) {
  try {
    const path = `/v1/api/yoyaku/${targetName}/${assignmentId}/is-reserve-extend`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    return false;
  }
}

export async function updateNotification(body: any) {
  try {
    const path = '/v1/api/notification';
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
    }
    throw new ServerError();
  }
}

export async function getCoupons(params: { targetName: string }): Promise<any[]> {
  const { targetName } = params;
  try {
    const path = `/v1/api/coupons/${targetName}`;
    const response = await request(path, 'get');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError(e);
  }
}

export async function createCoupon(params: { targetName: string }, body: any) {
  const { targetName } = params;
  try {
    const path = `/v1/api/coupons/${targetName}`;
    const response = await request(path, 'post', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function updateCoupon(params: { targetName: string; campaignCodeId: string }, body: any) {
  const { targetName, campaignCodeId } = params;
  try {
    const path = `/v1/api/coupons/${targetName}/${campaignCodeId}`;
    const response = await request(path, 'put', body);
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function deleteCoupon(params: { targetName: string; campaignCodeId: string }) {
  const { targetName, campaignCodeId } = params;
  try {
    const path = `/v1/api/coupons/${targetName}/${campaignCodeId}`;
    const response = await request(path, 'delete');
    return response.data;
  } catch (e) {
    if (e.response) {
      if (e.response.status === 401) {
        throw new AuthenticationError();
      }
      if (e.response.status === 403) {
        throw new AuthorizationError();
      }
    }
    throw new ServerError();
  }
}

export async function getSearchReserve(query: string) {
  const response = await request(`/v1/api/search/reserve?${query}`, 'get');
  return response.data;
}

export async function getYoyakuAccount(param: { targetName: string }) {
  const { targetName } = param;
  const path = `/v1/api/yoyaku/${targetName}/account`;
  const response = await request(path, 'get');
  return response.data;
}

export async function getYoyakuProperty(param: { targetName: string }) {
  const { targetName } = param;
  const path = `/v1/api/yoyaku/${targetName}/property`;
  const response = await request(path, 'get');
  return response.data;
}

export async function getYoyakuRooms(param: { targetName: string }) {
  const { targetName } = param;
  const path = `/v1/api/yoyaku/${targetName}/rooms`;
  const response = await request(path, 'get');
  return response.data;
}

export interface YoyakuDate {
  type: 'open' | 'closed' | 'out_of_period';
  date: string; // YYYY-MM-DD
  frames: YoyakuFrame[];
}

export interface YoyakuFrame {
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  amount: number;
  empty: boolean;
  warning: boolean;
}

export async function getYoyakuFrames(param: {
  targetName: string;
  roomId: string;
  startDate: string;
  length?: number;
}): Promise<YoyakuDate[] | null> {
  const { targetName, roomId, startDate } = param;
  const path = `/v1/api/yoyaku/${targetName}/frames`;
  let query = `?roomId=${roomId}&startDate=${startDate}`;
  if ('length' in param) {
    query += `&length=${param.length}`;
  }
  const response = await request(path + query, 'get');
  return response.data;
}

export async function getYoyakuResources(param: {
  targetName: string;
  roomId: string;
  startDate: string;
  startTime: string;
  endTime: string;
  isRegister?: boolean;
}) {
  const { targetName, roomId, startDate, startTime, endTime, isRegister } = param;
  const path = `/v1/api/yoyaku/${targetName}/resources`;
  let query = `?roomId=${roomId}&startDate=${startDate}&startTime=${startTime}&endTime=${endTime}`;
  if (isRegister !== undefined) {
    query += `&isRegister=${isRegister}`;
  }
  const response = await request(path + query, 'get');
  return response.data;
}

export async function getYoyakuCampaigns(param: {
  targetName: string;
  roomId: string;
  date: string;
  startTime: string;
  endTime: string;
  email?: string;
}): Promise<Campaign[]> {
  const { targetName, roomId, date, startTime, endTime, email } = param;
  const path = `/v1/api/yoyaku/${targetName}/campaigns`;
  const query = '?' + buildQueryParameterWithObject({ roomId, date, startTime, endTime, email });
  const response = await request(path + query, 'get');
  return response.data;
}

export async function createYoyakuReservation(
  param: { targetName: string },
  body: any,
): Promise<{ error?: string; yoyakuInfoUrl?: string; redirectUrl?: string }> {
  const { targetName } = param;
  const path = `/v1/api/yoyaku/${targetName}/reservations`;
  const response = await request(path, 'post', body);
  return response.data;
}

export interface ReqPayPayCapture {
  key: number;
  targetName: string;
  yoyakuInfo: any; // YYYY-MM-DD
  resourceIdList: any;
  resourceNameList: any;
  confirmationTokenId: string;
  campaignCode: string;
  paymentIntentId: string;
  paypayPaymentId: string;
  returnUrl: string;
}

export async function createYoyakuReservationPayPay(
    param: { targetName: string },
    body: ReqPayPayCapture,
): Promise<{ error?: string; yoyakuInfoUrl?: string; redirectUrl?: string }> {
  const { targetName } = param;
  const path = `/v1/api/yoyaku/${targetName}/reservations/paypay`;
  const response = await request(path, 'post', body);
  return response.data;
}


export interface YoyakuReservation {
  assignmentId: number;
  productName: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  amount: number;
  refundAmount: number;
  refundedAmount: number;
  userId: string;
  status: string;
  shareUrl: string;
  isReservingTime?: boolean;
  isExtend?: boolean;
  isConfigExtend?: boolean;
  extendReserveTimePrices?: ExtendReserveTimePrice[];
}

interface ExtendReserveTimePrice {
  key: string;
  value: string;
}

export async function getYoyakuReservation(param: {
  targetName: string;
  assignmentId: string;
}): Promise<{ ok?: YoyakuReservation; error?: 'not_found' }> {
  const { targetName, assignmentId } = param;
  const path = `/v1/api/yoyaku/${targetName}/reservations/${assignmentId}`;
  try {
    const response = await request(path, 'get');
    return { ok: response.data };
  } catch (e) {
    if (e.response.status === 404) {
      return { error: 'not_found' };
    }
    throw e;
  }
}

export async function getYoyakuReservationReceipt(
  targetName: string,
  assignmentId: string,
  body: any,
): Promise<{ ok?: any; error?: 'not_found' }> {
  const path = `/v1/api/yoyaku/${targetName}/reservations/${assignmentId}/receipt`;
  try {
    const response = await request(path, 'post', body, { responseType: 'blob' });
    return { ok: response.data };
  } catch (e) {
    if (e.response.status === 404) {
      return { error: 'not_found' };
    }
    throw e;
  }
}

export async function createYoyakuReservationCancel(param: { targetName: string; assignmentId: string }) {
  const { targetName, assignmentId } = param;
  const path = `/v1/api/yoyaku/${targetName}/reservations/${assignmentId}/cancel`;
  const response = await request(path, 'post');
  return response.data;
}

export interface StripeSetupIntent {
  id: string;
  client_secret: string | null;
}

export async function createStripeSetupIntent(param: { targetName: string }): Promise<StripeSetupIntent> {
  const { targetName } = param;
  const path = `/v1/api/stripe/${targetName}/setup_intents`;
  const response = await request(path, 'post');
  const { ok, error } = response.data;
  if (error) {
    throw new Error(error);
  }
  return ok;
}

export async function getStripeSetupIntent(param: { targetName: string; id: string }): Promise<StripeSetupIntent> {
  const { targetName, id } = param;
  const path = `/v1/api/stripe/${targetName}/setup_intents/${id}`;
  const response = await request(path, 'get');
  return response.data;
}

export async function cancelStripeSetupIntent(param: { targetName: string; id: string }): Promise<StripeSetupIntent> {
  const { targetName, id } = param;
  const path = `/v1/api/stripe/${targetName}/setup_intents/${id}/cancel`;
  const response = await request(path, 'post');
  const { ok, error } = response.data;
  if (error) {
    throw new Error(error);
  }
  return ok;
}

export async function createSecurityLog(body: { action: string; userName: string }) {
  const path = '/v1/api/security/log';
  const response = await request(path, 'post', body);
  return response.data;
}

interface RequestOption {
  upload?: boolean;
  getIp?: boolean;
  responseType?: ResponseType;
}

export async function request(path: string, method: any, data?: {}, option?: RequestOption, retry = 1) {
  const token = Store.getters.accessToken;
  const headers = await __headers(token, option);
  const responseType = option && option.responseType ? option.responseType : 'json';
  try {
    const url = `${process.env.VUE_APP_API_URL.indexOf('localhost') > 0 ? process.env.VUE_APP_API_URL : ''}${path}`;
    return await axios({
      url,
      method,
      headers,
      data,
      responseType: responseType,
      timeout: 100000,
      withCredentials: true,
    });
  } catch (e) {
    console.log('request error.' + path, e.response);
    // 401の場合はトークン期限切れなのでリフレッシュしてもう一度実行
    if (e.response.status === 401) {
      if (retry > 0) {
        const ok = await authenticate();
        if (ok) {
          return request(path, method, data, option, retry - 1);
        }
      }
    }
    throw e;
  }
}

async function __headers(token: string, option: RequestOption = {}) {
  const headers = {
    'Content-Type': option.upload ? 'multipart/form-data' : 'application/json',
    Authorization: token,
  };
  if (option.getIp) {
    const response = await axios.get('https://api.ipify.org?format=json');
    const { ip } = response.data;
    headers['x-forwarded-for'] = ip;
  }
  return headers;
}

export interface ReqPayPayCreateQRcode {
  targetName: string;
  amount: number;
  roomId: string;
  redirectUrl: string;
  redirectType: string;
  sessionData: any;
}

export interface ResPayPayCreateQRcode {
  rid: string;
  codeId: string;
  url: string;
  deeplink: string;
}

export interface ResPayPayError {
  message: string;
}

export async function createPayPayQRcode(targetName, param: ReqPayPayCreateQRcode) {
  const path = `/v1/api/paypay/${targetName}/qr-create`;
  const response = await request(path, 'post', param);
  const { data = null, message = null } = response.data;
  if (response.status === 200) {
    return data;
  } else {
    throw new Error(message ? message : 'エラーが発生しました');
  }
}

export interface ReqPayPayCheckStatus {
  merchantPaymentId: string;
}

export interface ResPayPayPaymentStatus {
  isCompleted: string;
  status: string;
  paymentId: string;
  merchantPaymentId: string;
}
export async function checkPayPayStatus(targetName, param: ReqPayPayCheckStatus) {
  const path = `/v1/api/paypay/${targetName}/status`;
  const response = await request(path, 'post', param);
  const { data = null, message = null } = response.data;
  if (response.status === 200) {
    return data;
  } else {
    throw new Error(message ? message : 'エラーが発生しました');
  }
}
export async function getReservingData(targetName) {
  const path = `/v1/api/paypay/${targetName}/reserving`;
  const response = await request(path, 'post');
  const { data = null, message = null } = response.data;
  if (response.status === 200) {
    return data;
  } else {
    throw new Error(message ? message : 'エラーが発生しました');
  }
}

export interface ReqPayPayPaymentData {
  rid: string;
  userId: string;
}

export async function makePaymentData(targetName, param: ReqPayPayPaymentData) {
  const path = `/v1/api/paypay/${targetName}/make-payment-data`;
  const response = await request(path, 'post', param);
  const { data = null, message = null } = response.data;
  if (response.status === 200) {
    return data;
  } else {
    throw new Error(message ? message : 'エラーが発生しました');
  }
}

export interface ResIsPayments {
  isPayPay: boolean;
}

export async function getIsPayments(targetName) {
  const path = `/v1/api/yoyaku/${targetName}/is-payments`;
  const response = await request(path, 'get');
  const { data = null, message = null } = response.data;
  if (response.status === 200) {
    return data;
  } else {
    throw new Error(message ? message : 'エラーが発生しました');
  }
}


export async function saveTemporaryReservation(targetName, param: any) {
    const path = `/v1/api/yoyaku/${targetName}/temporary-reservation`;
    const response = await request(path, 'post', param);
    const { message = null } = response.data;
    if (response.status === 200) {
        return response.data;
    } else {
        throw new Error(message ? message : 'エラーが発生しました');
    }
}

export interface ReqCancelTemporaryReservation {
    rid: string;
}

export async function cancelTemporaryReservation(targetName, param: ReqCancelTemporaryReservation) {
    const path = `/v1/api/yoyaku/${targetName}/temporary-reservation-cancel`;
    const response = await request(path, 'post', param);
    const { message = null } = response.data;
    if (response.status === 200) {
        return true;
    } else {
        throw new Error(message ? message : 'エラーが発生しました');
    }
}

export async function updateSpecialDayRemark(targetName, body: ReqSpecialDayRemark.Body) {
  const path = `/v1/api/property/${targetName}/special-day`;

  try {
    await request(path, 'post', body);
  } catch (e) {
    throw new ServerError();
  }
}
